'use client';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Draggable } from 'gsap/Draggable';
import CursorTooltip from './CursorTooltip';

if (typeof window !== 'undefined') {
	gsap.registerPlugin(ScrollTrigger, Draggable);
}

interface OverlappingCardItem {
	id: string | number;
	imageSrc: string;
	alt: string;
	title?: string;
	subtitle?: string;
	extraLine?: string;
}

interface OverlappingCardsProps {
	items: OverlappingCardItem[];
	className?: string;
	cardWidth?: number; // px
	cardHeight?: number; // px
	overlapPx?: number; // how much each subsequent card overlaps to the left (positive number)
	radiusPx?: number;
	showShadow?: boolean;
	maxPerRow?: number; // maximum cards per row
	hoverBackgroundColor?: string; // background color when hovered
	showTooltip?: boolean; // whether to show tooltip on hover
}

export default function OverlappingCards({
	items,
	className,
	cardWidth = 280,
	cardHeight = 340,
	overlapPx = 24,
	radiusPx = 12,
	showShadow = false,
	maxPerRow = 5,
	hoverBackgroundColor,
	showTooltip = true,
}: OverlappingCardsProps) {
	const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
	const [hoverRotation, setHoverRotation] = useState<number>(0);
	const [isMobile, setIsMobile] = useState(false); // Always false initially to match server render
	const [screenWidth, setScreenWidth] = useState<number | null>(null);
	const [cardOrder, setCardOrder] = useState<number[]>([]);
	const [draggingCardIndex, setDraggingCardIndex] = useState<number | null>(null);
	const [adjustedOverlap, setAdjustedOverlap] = useState<number>(overlapPx * 0.5);
	const containerRef = useRef<HTMLDivElement>(null);
	const cardsRef = useRef<(HTMLElement | null)[]>([]);
	const desktopContainerRef = useRef<HTMLDivElement>(null);
	const hasInteractedRef = useRef(false);

	// Calculate responsive maxPerRow: 3 per row between md (48rem/768px) and 1440px
	const effectiveMaxPerRow = React.useMemo(() => {
		if (screenWidth === null || screenWidth < 768) return maxPerRow;
		if (screenWidth >= 768 && screenWidth < 1440) {
			// Between md (48rem/768px) and 1440px: 3 cards per row for both homepage and about page
			return 3;
		}
		return maxPerRow;
	}, [screenWidth, maxPerRow]);

	// Detect mobile and track screen width - only after mount to prevent hydration mismatch
	useEffect(() => {
		if (typeof window === 'undefined') return;
		
		const updateScreenInfo = () => {
			const width = window.innerWidth;
			setIsMobile(width <= 768);
			setScreenWidth(width);
		};
		
		// Check after mount (after hydration)
		updateScreenInfo();
		
		// Listen for resize events
		window.addEventListener('resize', updateScreenInfo);
		
		return () => {
			window.removeEventListener('resize', updateScreenInfo);
		};
	}, []);

	// Calculate responsive overlap for desktop to fit within container
	useEffect(() => {
		if (isMobile || !desktopContainerRef.current) return;

		const calculateOverlap = () => {
			const container = desktopContainerRef.current;
			if (!container) return;

			// Get the container width (accounting for global-padding)
			const containerWidth = container.offsetWidth;
			
			// Calculate how many cards are in the first row
			const cardsInRow = Math.min(items.length, effectiveMaxPerRow);
			
			if (cardsInRow <= 1) {
				setAdjustedOverlap(overlapPx * 0.5);
				return;
			}

			// Calculate overlap to make cards span exactly to the right edge
			// Total width = cardWidth + (cardsInRow - 1) * (cardWidth - overlap)
			// Solving for overlap: overlap = (cardsInRow * cardWidth - containerWidth) / (cardsInRow - 1)
			const totalCardWidth = cardsInRow * cardWidth;
			const spaceNeeded = totalCardWidth - containerWidth;
			
			// Calculate exact overlap needed to fill container width
			// If negative, cards will have spacing between them (which is fine)
			const exactOverlap = spaceNeeded / (cardsInRow - 1);
			
			// Cap overlap at 90% of card width to prevent cards from overlapping too much
			// But allow negative values (spacing) for wide containers
			const maxOverlap = cardWidth * 0.9;
			
			// Use the calculated overlap
			// If exactOverlap is negative (spacing needed), use it directly
			// If positive (overlap needed), cap it at maxOverlap
			const finalOverlap = exactOverlap < 0 ? exactOverlap : Math.min(exactOverlap, maxOverlap);
			
			setAdjustedOverlap(finalOverlap);
		};

		calculateOverlap();
		window.addEventListener('resize', calculateOverlap);
		
		// Also recalculate when items change
		const observer = new ResizeObserver(calculateOverlap);
		if (desktopContainerRef.current) {
			observer.observe(desktopContainerRef.current);
		}

		return () => {
			window.removeEventListener('resize', calculateOverlap);
			observer.disconnect();
		};
	}, [isMobile, items.length, effectiveMaxPerRow, cardWidth, overlapPx]);

	// Initialize card order for mobile stack
	useEffect(() => {
		setCardOrder(items.map((_, i) => i));
	}, [items.length]);

	// Mobile card stack with swipe-to-dismiss
	useEffect(() => {
		if (!isMobile || items.length === 0 || cardOrder.length === 0) return;

		const cards = cardsRef.current.filter(Boolean) as HTMLElement[];
		if (cards.length === 0) return;

		const draggableInstances: Draggable[] = [];

		const ctx = gsap.context(() => {
			const threshold = 120; // pixels to swipe before card dismisses

			cards.forEach((card, index) => {
				// Set initial stacking
				const stackIndex = cardOrder.indexOf(index);
				const isTopCard = stackIndex === 0;

				// Add alternating rotation for cards behind the top card
				let rotation = 0;
				if (stackIndex === 1) rotation = -5; // Second card tilts left
				if (stackIndex === 2) rotation = 5; // Third card tilts right

				gsap.set(card, {
					zIndex: cards.length - stackIndex,
					scale: 1 - stackIndex * 0.04,
					y: stackIndex * 16,
					x: 0,
					rotation: rotation,
					opacity: stackIndex < 3 ? 1 : 0,
				});

				// Only make the top card draggable
				if (isTopCard) {
					const instance = Draggable.create(card, {
						type: 'x',
						inertia: true,
						onDragStart: function () {
							setDraggingCardIndex(index);
							// Stop hint animation
							gsap.killTweensOf(card, 'rotation');
							hasInteractedRef.current = true;
						},
						onDrag: function () {
							const x = this.x;
							const rotation = x / 20;
							gsap.set(card, { rotation });
						},
						onDragEnd: function () {
							const x = this.x;
							if (Math.abs(x) > threshold) {
								const direction = x > 0 ? 1 : -1;
								gsap.to(card, {
									x: direction * window.innerWidth,
									rotation: direction * 30,
									opacity: 0,
									duration: 0.3,
									ease: 'power2.in',
									onComplete: () => {
										setDraggingCardIndex(null);
										setCardOrder((prev) => {
											const newOrder = [...prev];
											const removedCard = newOrder.shift()!;
											newOrder.push(removedCard);
											return newOrder;
										});
									},
								});
							} else {
								gsap.to(card, {
									x: 0,
									y: 0,
									rotation: 0,
									duration: 0.3,
									ease: 'elastic.out(1, 0.5)',
									onComplete: () => {
										setDraggingCardIndex(null);
									},
								});
							}
						},
					})[0];

					draggableInstances.push(instance);

					// Hint animation
					if (!hasInteractedRef.current) {
						gsap.to(card, {
							keyframes: { rotation: [2, -2, 2] },
							duration: 2,
							repeat: -1,
							ease: 'sine.inOut',
							delay: 0.5,
						});
					}
				}
			});
		}, containerRef);

		return () => {
			ctx.revert();
			draggableInstances.forEach((d) => d.kill());
		};
	}, [isMobile, items.length, cardOrder]);

	const getRandomRotation = () => Number((Math.random() * 4 - 2).toFixed(2));

	function chunk<T>(arr: T[], size: number): T[][] {
		const out: T[][] = [];
		for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
		return out;
	}

	// Mobile view with swipeable card stack
	if (isMobile) {
		// Responsive card sizing for mobile
		const mobileCardWidth = Math.min(
			cardWidth,
			typeof window !== 'undefined' ? window.innerWidth * 0.85 : cardWidth
		);
		const mobileCardHeight = 378;

		return (
			<div
				ref={containerRef}
				className={['w-full relative', className].filter(Boolean).join(' ')}
			>
				{/* Card stack container */}
				<div className="relative w-full flex justify-center px-4">
					<div
						className="relative"
						style={{
							width: mobileCardWidth,
							height: mobileCardHeight + 40,
						}}
					>
						{/* Render cards in stacked order */}
						{items.map((item, index) => {
							const isTopCard = cardOrder.length > 0 && cardOrder[0] === index;
							return (
								<article
									key={item.id}
									ref={(el) => {
										cardsRef.current[index] = el;
									}}
									className={`absolute top-0 left-0 overflow-hidden bg-[#FDFEF6] flex flex-col cursor-grab active:cursor-grabbing touch-pan-y ${isTopCard ? '' : 'border-2 border-black'}`}
									style={{
										width: mobileCardWidth,
										height: mobileCardHeight,
										borderRadius: radiusPx,
										padding: 16,
										willChange: 'transform',
										backgroundColor: isTopCard && hoverBackgroundColor ? hoverBackgroundColor : '#FDFEF6',
										border: isTopCard ? 'none' : undefined,
									}}
									aria-label={item.title || item.alt}
									tabIndex={0}
								>
								<div
									className={`relative overflow-hidden pointer-events-none ${!(item.title || item.subtitle) ? 'flex-1' : ''}`}
									style={{ 
										borderRadius: Math.max(0, radiusPx - 4),
										...(item.title || item.subtitle ? { height: '18.5rem' } : {}),
									}}
								>
									<Image
										src={item.imageSrc}
										alt={item.alt}
										fill
										sizes={`${mobileCardWidth}px`}
										className="object-cover"
										draggable={false}
									/>
								</div>
								{(item.title || item.subtitle) && (
									<div className="flex flex-col mt-2 pointer-events-none">
										{item.subtitle && (
											<p className="eyebrow-sm">{item.subtitle}</p>
										)}
										{item.title && (
											<h3 className="body-default mt-1">{item.title}</h3>
										)}
									</div>
								)}
							</article>
							);
						})}
					</div>
				</div>
			</div>
		);
	}

	// Desktop view with hover effects
	const rows = chunk(items, Math.max(1, effectiveMaxPerRow));

	return (
		<div 
			ref={desktopContainerRef}
			className={['w-full flex flex-col items-start', className].filter(Boolean).join(' ')}
		>
			{rows.map((row, rowIdx) => {
				return (
					<ul key={rowIdx} className={["flex items-center justify-start w-full", rowIdx > 0 ? 'mt-[3.4375rem]' : ''].join(' ')}>
						{row.map((item, index) => {
							const globalIndex = rowIdx * effectiveMaxPerRow + index;
							const handleFocus = () => {
								setHoveredIndex(globalIndex);
								setHoverRotation(getRandomRotation());
							};

							const handleBlur = () => {
								setHoveredIndex(null);
								setHoverRotation(0);
							};

							const handleKeyDown = (e: React.KeyboardEvent) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									// Toggle hover state for keyboard users
									if (hoveredIndex === globalIndex) {
										setHoveredIndex(null);
										setHoverRotation(0);
									} else {
										setHoveredIndex(globalIndex);
										setHoverRotation(getRandomRotation());
									}
								}
							};

							const cardContent = (
								<article
									className={`relative overflow-visible bg-[#FDFEF6] transition-all duration-200 ease-out will-change-transform flex flex-col focus:outline-none focus:ring-2 focus:ring-blue-500 ${hoveredIndex === globalIndex ? '' : 'border-2 border-black'}`}
									style={{
										width: cardWidth,
									height: cardHeight,
									borderRadius: radiusPx,
									padding: '1.25rem',
									zIndex: hoveredIndex === globalIndex ? 10 : 1,
									boxShadow: showShadow ? '0 8px 24px rgba(0,0,0,0.12)' : 'none',
									transform:
										hoveredIndex === globalIndex
											? `rotate(${hoverRotation}deg) scale(1.07) translateY(-0.3125rem)`
											: 'rotate(0deg) scale(1) translateY(0)',
										transformOrigin: 'center center',
										backgroundColor: hoveredIndex === globalIndex && hoverBackgroundColor ? hoverBackgroundColor : '#FDFEF6',
										transition: 'background-color 0.15s ease-out, transform 0.2s ease-out',
										border: hoveredIndex === globalIndex ? 'none' : undefined,
									}}
									onMouseEnter={handleFocus}
									onMouseLeave={handleBlur}
									onFocus={handleFocus}
									onBlur={handleBlur}
									onKeyDown={handleKeyDown}
									tabIndex={0}
									role="button"
									aria-label={item.title || item.alt}
									aria-pressed={hoveredIndex === globalIndex}
								>
									<div
										className={`relative overflow-hidden ${!(item.title || item.subtitle) ? 'flex-1' : ''}`}
										style={{ 
											borderRadius: Math.max(0, radiusPx - 4),
											...(item.title || item.subtitle ? { height: '18.5rem' } : {}),
										}}
									>
										<Image
											src={item.imageSrc}
											alt={item.alt}
											fill
											sizes={`${cardWidth}px`}
											className="object-cover"
										/>
									</div>
									{(item.title || item.subtitle) && (
										<div className="flex flex-col justify-between flex-1 mt-3">
											{item.subtitle && (
												<p className="eyebrow-sm">{item.subtitle}</p>
											)}
											{item.title && (
												<h3 className="body-default">{item.title}</h3>
											)}
										</div>
									)}
								</article>
							);

							return (
								<li
									key={item.id}
									className="relative"
									style={{ 
										marginLeft: index === 0 ? 0 : -adjustedOverlap,
									}}
								>
									{showTooltip && item.extraLine ? (
										<CursorTooltip
											tooltipText={item.extraLine}
											backgroundColor="#FACC15"
											textColor="#000000"
										>
											{cardContent}
										</CursorTooltip>
									) : (
										cardContent
									)}
								</li>
							);
						})}
					</ul>
				);
			})}

		</div>
	);
}
